import React, { useEffect, useMemo, useRef, useState } from "react"
import { ScreenSkeleton } from "../../common/screens/ScreenSkeleton"
import { CheckoutProvider } from "@stripe/react-stripe-js/checkout"
import { useNavigation, useRoute } from "@react-navigation/native"
import { connect } from "react-redux"
import { apiRequest } from "../../data/redux/actions/api"
import { ChangePlanBottomModal } from "./ChangePlanBottomModal"
import { CreateNewSubscriptionScreenContent } from "./CreateNewSubscriptionScreenContent"
import { useNewSubscriberScreenRedirection } from "../hooks/useNewSubscriberScreenRedirection"
import { useGenerateCheckoutSession } from "./hooks/useGenerateCheckoutSession"
import { useCreateNewSubscriptionState } from "./hooks/useCreateNewSubscriptionState"

// Funnel param routes the magic-link entry to the right resume handler so we
// can pivot brevet/summer/holiday entries via JS instead of claiming new native
// paths. `funnel=brevet` → brevet/resume, `funnel=summer` → summer/resume.
const FUNNEL_RESUME_ROUTES = {
    brevet: "brevet/resume",
    summer: "summer/resume",
    inscription: "inscription/resume",
}

/**
 * Funnel vers lequel bascule un lien SANS `funnel=`.
 *
 * L'ancien paywall n'est plus le chemin self-service de l'abonnement régulier :
 * le funnel d'inscription l'est. Tous les liens historiques qui pointent encore
 * ici — le « copier le lien » du BO, la propriété HubSpot `self_subscription_link`
 * des séquences de nurturing, le CTA de l'email post-diagnostic — arrivent donc
 * sur le nouveau parcours sans qu'il faille les réécrire un par un.
 */
const DEFAULT_FUNNEL = "inscription"

const useFunnelRedirect = ({ canDefault }) => {
    const navigation = useNavigation()
    const route = useRoute()
    const readParam = (name) =>
        route?.params?.[name] ??
        (typeof window !== "undefined"
            ? new URLSearchParams(window.location?.search || "").get(name)
            : null)
    const funnel = readParam("funnel")
    const userParam = readParam("user")
    // Forward the summer fast-track link params so the magic link's engagement /
    // no-deposit choice survives the create-subscription → resume hop (otherwise the
    // parent lands on the default tarif + 1 €).
    const engagement = readParam("engagement")
    const sansDepot = readParam("sans_depot")

    // Un `funnel=` explicite gagne toujours et part TOUT DE SUITE : il désigne
    // un parcours déjà entamé, aucune donnée serveur n'est nécessaire pour le
    // router. Le défaut, lui, attend `canDefault` — c'est-à-dire que le
    // fast-track du lead soit lu et qu'aucune redirection plus spécifique ne
    // s'applique : ni lien staged admin (qui porte une offre pré-configurée que
    // le funnel écraserait), ni famille déjà abonnée (que le funnel refuserait
    // au paiement). Basculer avant cette résolution casserait ces deux chemins.
    const resumeRoute = funnel
        ? FUNNEL_RESUME_ROUTES[funnel]
        : canDefault
          ? FUNNEL_RESUME_ROUTES[DEFAULT_FUNNEL]
          : null

    useEffect(() => {
        if (resumeRoute && userParam) {
            const params = { user: userParam }
            if (engagement != null) params.engagement = engagement
            if (sansDepot != null) params.sans_depot = sansDepot
            navigation.replace(resumeRoute, params)
        }
    }, [resumeRoute, userParam, engagement, sansDepot, navigation])
    return !!resumeRoute && !!userParam
}

const CreateNewSubscriptionScreen = (props) => {
    const bottomSheetRef = useRef()
    const hasOpenedModalRef = useRef(false)
    const [checkoutSession, setCheckoutSession] = useState(null)
    const {
        stripePromise,
        plan,
        loadingUser,
        email,
        emailB64,
        user,
        fastTrackData,
        handleChangePlan,
    } = useCreateNewSubscriptionState(props, false)

    const openEditPlanModal = () => bottomSheetRef.current?.present()
    const closeEditPlanModal = () => bottomSheetRef.current?.dismiss()

    const { willRedirect } = useNewSubscriberScreenRedirection({
        fastTrackData: loadingUser ? null : fastTrackData,
        emailB64,
        redirectIfCreatedByAdmin: true,
    })

    // Le défaut ne s'arme qu'une fois le fast-track lu ET aucune redirection
    // plus spécifique retenue — voir useFunnelRedirect.
    const willRedirectToFunnel = useFunnelRedirect({
        canDefault: !loadingUser && !!fastTrackData && !willRedirect,
    })

    useGenerateCheckoutSession({
        plan,
        apiRequest: props.apiRequest,
        isLoading: loadingUser || willRedirect || willRedirectToFunnel,
        email,
        schoolSubjects: props.schoolSubjects,
        setCheckoutSession,
        pricingTable: props.pricingTable,
        sessionsPerWeek: props.sessionsPerWeek,
    })

    useEffect(() => {
        if (loadingUser || !checkoutSession?.client_secret) return
        if (hasOpenedModalRef.current) return
        hasOpenedModalRef.current = true
        const timer = setTimeout(() => openEditPlanModal(), 500)
        return () => clearTimeout(timer)
    }, [loadingUser, checkoutSession?.client_secret])

    const loadingMessage = useMemo(() => {
        if (willRedirectToFunnel) return "Reprise du parcours en cours…"
        if (loadingUser || willRedirect)
            return "Chargement de vos informations..."
        if (props.isLoadingCheckout) return "Préparation de votre formule..."
        if (!checkoutSession?.client_secret) return "Finalisation en cours..."
        return null
    }, [
        willRedirectToFunnel,
        loadingUser,
        willRedirect,
        props.isLoadingCheckout,
        checkoutSession?.client_secret,
    ])

    return (
        <ScreenSkeleton
            errorScreenProps={{ error: props.error || props.errorCheckout }}
            loadingMessage={loadingMessage}
            isLoading={
                willRedirectToFunnel ||
                loadingUser ||
                props.isLoadingCheckout ||
                willRedirect ||
                !checkoutSession?.client_secret
            }
        >
            <CheckoutProvider
                key={checkoutSession?.client_secret}
                stripe={stripePromise}
                options={{
                    fetchClientSecret: async () =>
                        checkoutSession.client_secret,
                }}
            >
                <CreateNewSubscriptionScreenContent
                    plan={plan}
                    schoolSubjects={props.schoolSubjects}
                    openEditPlanModal={openEditPlanModal}
                    pricingTable={props.pricingTable}
                    sessionsPerWeek={props.sessionsPerWeek}
                    user={user}
                    checkoutSession={checkoutSession}
                />
            </CheckoutProvider>
            <ChangePlanBottomModal
                schoolSubjects={props.schoolSubjects}
                pricingTable={props.pricingTable}
                sessionsPerWeek={props.sessionsPerWeek}
                selfServeNoCommitmentHours={props.selfServeNoCommitmentHours}
                closeModal={closeEditPlanModal}
                plan={plan}
                setPlan={handleChangePlan}
                bottomModalRef={bottomSheetRef}
                studentClass={user?.profile?.class}
            />
        </ScreenSkeleton>
    )
}

const mapStateToProps = (state) => {
    return {
        error: state.api.getSubscribePricing?.error,
        pricingTable: state.data.formData?.pricingTableV2,
        sessionsPerWeek: state.data.formData?.sessionsPerWeek,
        selfServeNoCommitmentHours:
            state.data.formData?.selfServeNoCommitmentHours,
        schoolSubjects: state.data.formData?.schoolSubjects ?? [],
        isLoadingCheckout: state.api.selfCreateSubscription?.request,
        errorCheckout: state.api.selfCreateSubscription?.error,
    }
}

const mapDispatchToProps = {
    apiRequest,
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(CreateNewSubscriptionScreen)
